// FILE: src/components/security/MFASettings.js
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  encryptSecret,
  generateBackupCodes,
  generateQrForSecret,
  generateTotpSecret,
  loadMfaProfile,
  saveMfaProfile,
  verifyTotp,
} from "../../utils/mfa";
import { auth } from "../../firebase";

async function authFetch(path, options = {}) {
  const u = auth.currentUser;
  if (!u) throw new Error("Not authenticated");
  const token = await u.getIdToken();
  const headers = {
    ...(options.headers || {}),
    authorization: `Bearer ${token}`,
    "content-type": "application/json",
  };
  const res = await fetch(path, { ...options, headers });
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const msg = data?.error || data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export default function MFASettings() {
  const [status, setStatus] = useState({ loading: true, enabled: false, method: "auth" });
  const [err, setErr] = useState("");

  // Authenticator (TOTP) enroll state
  const [qr, setQr] = useState({ url: "", otpauth: "", secret: "" });
  const [totpCode, setTotpCode] = useState("");

  // Email enroll state
  const [emailEnroll, setEmailEnroll] = useState({
    active: false,
    sent: false,
    cooldown: 0,
    code: "",
  });

  // Recovery codes to show once
  const [recovery, setRecovery] = useState([]);

  const methodLabel = useMemo(() => {
    if (!status.enabled) return "—";
    return status.method === "email" ? "Email codes" : "Authenticator app";
  }, [status.enabled, status.method]);

  async function fetchStatus() {
    setErr("");
    try {
      const u = auth.currentUser;
      const profile = await loadMfaProfile(u?.uid);
      const enabled = !!profile?.enabled;

      // Infer method safely:
      // - Prefer explicit profile.method if present
      // - Otherwise, if secretEnc exists -> authenticator
      // - Otherwise -> email
      const inferred =
        profile?.method ||
        (profile?.secretEnc ? "auth" : "email");

      setStatus({ loading: false, enabled, method: inferred === "email" ? "email" : "auth" });
    } catch (e) {
      setStatus({ loading: false, enabled: false, method: "auth" });
      setErr(e?.message || "Failed to load MFA status");
    }
  }

  useEffect(() => { fetchStatus(); }, []);

  // Cooldown timer for email resend
  useEffect(() => {
    if (!emailEnroll.sent || emailEnroll.cooldown <= 0) return;
    const t = setInterval(() => {
      setEmailEnroll((p) => {
        if (!p.sent || p.cooldown <= 0) return p;
        return { ...p, cooldown: Math.max(0, p.cooldown - 1) };
      });
    }, 1000);
    return () => clearInterval(t);
  }, [emailEnroll.sent, emailEnroll.cooldown]);

  // ---------- Enable with Email ----------
  async function startEnrollEmail() {
    setErr("");
    setRecovery([]);
    setQr({ url: "", otpauth: "", secret: "" });
    setTotpCode("");

    try {
      setEmailEnroll({ active: true, sent: false, cooldown: 0, code: "" });
      await authFetch("/api/mfa/sendEmailCode", { method: "POST", body: JSON.stringify({}) });
      setEmailEnroll({ active: true, sent: true, cooldown: 45, code: "" });
    } catch (e) {
      setEmailEnroll({ active: false, sent: false, cooldown: 0, code: "" });
      setErr(e?.message || "Failed to send code.");
    }
  }

  async function resendEmailCode() {
    setErr("");
    try {
      await authFetch("/api/mfa/sendEmailCode", { method: "POST", body: JSON.stringify({}) });
      setEmailEnroll((p) => ({ ...p, sent: true, cooldown: 45 }));
    } catch (e) {
      setErr(e?.message || "Failed to resend code.");
    }
  }

  async function confirmEnrollEmail(e) {
    e.preventDefault();
    setErr("");
    try {
      const code = String(emailEnroll.code || "").trim();
      if (!/^\d{6}$/.test(code)) throw new Error("Enter the 6-digit code from your email.");

      await authFetch("/api/mfa/verifyEmailCode", {
        method: "POST",
        body: JSON.stringify({ code }),
      });

      const u = auth.currentUser;
      if (!u) throw new Error("Not authenticated");
      const profile = await loadMfaProfile(u.uid);

      // Generate recovery codes
      const { plain, hashes } = generateBackupCodes();

      await saveMfaProfile(u.uid, {
        ...profile,
        enabled: true,
        method: "email",
        // keep any existing secretEnc if present (so switching back doesn't lose data)
        recovery: { hashes },
        backupCodeHashes: hashes,
        trustedDevices: profile?.trustedDevices || [],
      });

      setRecovery(plain);
      setEmailEnroll({ active: false, sent: false, cooldown: 0, code: "" });
      await fetchStatus();
    } catch (e) {
      setErr(e?.message || "Failed to confirm email code.");
    }
  }

  // ---------- Enable with Authenticator ----------
  async function startEnrollAuthenticator() {
    setErr("");
    setRecovery([]);
    setEmailEnroll({ active: false, sent: false, cooldown: 0, code: "" });
    setTotpCode("");

    try {
      const u = auth.currentUser;
      const secret = generateTotpSecret();
      const { qrDataUrl, otpauth } = await generateQrForSecret({
        label: u?.email || "user",
        secret,
      });
      setQr({ url: qrDataUrl, otpauth, secret });
    } catch (e) {
      setErr(e?.message || "Start failed");
    }
  }

  async function confirmEnrollAuthenticator(e) {
    e.preventDefault();
    setErr("");
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not authenticated");
      if (!qr.secret) throw new Error("No secret to verify.");

      const code = String(totpCode || "").trim();
      if (!/^\d{6}$/.test(code)) throw new Error("Enter your 6-digit authenticator code.");

      const ok = verifyTotp(qr.secret, code);
      if (!ok) throw new Error("Invalid code. Please try again.");

      const profile = await loadMfaProfile(u.uid);
      const secretEnc = encryptSecret(qr.secret);

      // Recovery codes
      const { plain, hashes } = generateBackupCodes();

      await saveMfaProfile(u.uid, {
        ...profile,
        enabled: true,
        method: "auth",
        secretEnc,
        recovery: { hashes },
        backupCodeHashes: hashes,
        trustedDevices: profile?.trustedDevices || [],
      });

      setRecovery(plain);
      setQr({ url: "", otpauth: "", secret: "" });
      setTotpCode("");
      await fetchStatus();
    } catch (e) {
      setErr(e?.message || "Confirm failed");
    }
  }

  // ---------- Actions when enabled ----------
  async function regenerateRecovery() {
    setErr("");
    setRecovery([]);
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not authenticated");
      const profile = await loadMfaProfile(u.uid);

      const { plain, hashes } = generateBackupCodes();
      await saveMfaProfile(u.uid, {
        ...profile,
        recovery: { hashes },
        backupCodeHashes: hashes,
      });

      setRecovery(plain);
    } catch (e) {
      setErr(e?.message || "Regenerate failed");
    }
  }

  const isSwitchingToAuth = status.enabled && status.method === "email";
  const isSwitchingToEmail = status.enabled && status.method === "auth";

  return (
    <div className="ad-card mfa-stack" style={{ maxWidth: 640 }}>
      <h3>Two-Factor Authentication</h3>

      {err ? <div className="ad-banner ad-banner--error">{err}</div> : null}

      {status.loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="ad-kv">
            <span>Status</span>
            <span className={`mfa-status-val ${status.enabled ? "mfa-status-val--on" : "mfa-status-val--off"}`}>
              {status.enabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          <div className="ad-kv" style={{ marginTop: 6 }}>
            <span>Method</span>
            <span>{methodLabel}</span>
          </div>

          {/* Enable buttons (shown when disabled) */}
          {!status.enabled && !qr.url && !emailEnroll.active && (
            <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
              <button
                className="ad-btn mfa-btn mfa-btn--email"
                onClick={startEnrollEmail}
                type="button"
              >
                Enable with Email Codes
              </button>

              <button
                className="ad-btn mfa-btn mfa-btn--auth"
                onClick={startEnrollAuthenticator}
                type="button"
              >
                Enable with Authenticator App
              </button>
            </div>
          )}

          {/* Email enroll panel (can appear even when enabled if switching) */}
          {emailEnroll.active ? (
            <div style={{ marginTop: 12 }}>
              <div className="ad-kv">
                <span>Email verification</span>
                <span>{emailEnroll.sent ? "Code sent" : "Sending…"}</span>
              </div>

              <form className="ad-row ad-gap ad-wrap" style={{ marginTop: 10 }} onSubmit={confirmEnrollEmail}>
                <input
                  className="ad-input mfa-input"
                  placeholder="Enter 6-digit code"
                  inputMode="numeric"
                  value={emailEnroll.code}
                  onChange={(e) => setEmailEnroll((p) => ({ ...p, code: e.target.value }))}
                />

                <button className="ad-btn ad-btn--primary mfa-btn mfa-btn--primary" type="submit">
                  Confirm
                </button>

                <button
                  className="ad-btn mfa-btn mfa-btn--ghost"
                  type="button"
                  onClick={() => setEmailEnroll({ active: false, sent: false, cooldown: 0, code: "" })}
                >
                  Cancel
                </button>

                <button
                  className="ad-btn mfa-btn mfa-btn--email"
                  type="button"
                  onClick={resendEmailCode}
                  disabled={emailEnroll.cooldown > 0}
                  title={emailEnroll.cooldown > 0 ? `Wait ${emailEnroll.cooldown}s` : "Resend code"}
                >
                  {emailEnroll.cooldown > 0 ? `Resend (${emailEnroll.cooldown}s)` : "Resend code"}
                </button>
              </form>

              {status.enabled ? (
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 8 }}>
                  You’re switching your MFA method to <b>Email codes</b>. Confirming will update your method.
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Authenticator QR panel (can appear even when enabled if switching) */}
          {qr.url ? (
            <div style={{ marginTop: 12 }}>
              <div className="ad-kv">
                <span>Scan in your authenticator app</span>
                <span>—</span>
              </div>

              <div style={{ display: "grid", placeItems: "center", padding: 12 }}>
                <img
                  src={qr.url}
                  alt="MFA QR"
                  style={{ width: 220, height: 220, maxWidth: "100%" }}
                />
              </div>

              {/* ✅ Mobile-friendly: manual setup key (for enabling on the same phone) */}
              <div className="mfa-manual">
                <div className="mfa-manual__title">Can’t scan this QR code?</div>
                <div className="mfa-manual__text">
                  In your authenticator app, choose <b>Enter a setup key</b> (or <b>Manual entry</b>) and paste this key:
                </div>
                <div className="mfa-manual__row">
                  <code className="mfa-manual__code">{qr.secret}</code>
                  <button
                    type="button"
                    className="ad-btn mfa-btn mfa-btn--ghost mfa-copy-btn"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(qr.secret);
                      } catch {
                        const ta = document.createElement("textarea");
                        ta.value = qr.secret;
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand("copy");
                        document.body.removeChild(ta);
                      }
                    }}
                  >
                    Copy key
                  </button>
                </div>
                <div className="mfa-manual__hint">Type: <b>Time-based (TOTP)</b></div>
              </div>

              <form className="ad-row ad-gap ad-wrap" style={{ marginTop: 8 }} onSubmit={confirmEnrollAuthenticator}>
                <input
                  className="ad-input mfa-input"
                  placeholder="Enter 6-digit code"
                  inputMode="numeric"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                />
                <button className="ad-btn ad-btn--primary mfa-btn mfa-btn--primary" type="submit">
                  Confirm
                </button>
                <button
                  className="ad-btn mfa-btn mfa-btn--ghost"
                  type="button"
                  onClick={() => {
                    setQr({ url: "", otpauth: "", secret: "" });
                    setTotpCode("");
                  }}
                >
                  Cancel
                </button>
              </form>

              {status.enabled ? (
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 8 }}>
                  You’re switching your MFA method to <b>Authenticator app</b>. Confirming will update your method.
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Enabled actions */}
          {status.enabled && !emailEnroll.active && !qr.url ? (
            <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
              <button className="ad-btn mfa-btn mfa-btn--success" onClick={regenerateRecovery} type="button">
                Re-Generate recovery codes
              </button>

              {isSwitchingToAuth ? (
                <button className="ad-btn mfa-btn mfa-btn--success" onClick={startEnrollAuthenticator} type="button">
                  Switch to Authenticator App
                </button>
              ) : null}

              {isSwitchingToEmail ? (
                <button className="ad-btn mfa-btn mfa-btn--success" onClick={startEnrollEmail} type="button">
                  Switch to Email Codes
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Recovery codes display */}
          {recovery.length ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Recovery codes (save these now):</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {recovery.map((c) => (
                  <li key={c}>
                    <code>{c}</code>
                  </li>
                ))}
              </ul>
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 8 }}>
                Store these in a safe place. Each code can be used once if you lose access to your authenticator.
              </div>
            </div>
          ) : null}
        </>
      )}

      <style>{`
        .mfa-stack .ad-banner{margin-top:10px}
        .mfa-stack .ad-kv{display:flex;justify-content:space-between;gap:10px}
        .mfa-stack .ad-row{display:flex;align-items:center}
        .mfa-stack .ad-gap{gap:10px}
        .mfa-stack .ad-wrap{flex-wrap:wrap}

        /* Modern input */
        .mfa-stack .mfa-input{
          width: 240px;
          border-radius: 12px !important;
          border: 1px solid #e5e7eb !important;
          padding: 12px 14px !important;
          min-height: 46px !important;
          font-weight: 700 !important;
          outline: none !important;
          transition: border-color .12s ease, box-shadow .12s ease;
          background:#fff !important;
        }
        .mfa-stack .mfa-input:focus{
          border-color:#2a0516 !important;
          box-shadow:0 0 0 3px rgba(42,5,22,.14) !important;
        }

        /* Base modern button */
        .mfa-stack .mfa-btn{
          border-radius: 12px !important;
          min-height: 44px !important;
          font-weight: 800 !important;
          border: 1px solid transparent !important;
          box-shadow: 0 10px 24px rgba(0,0,0,.06) !important;
          transition: transform .12s ease, box-shadow .12s ease, filter .12s ease, background .12s ease;
        }
        .mfa-stack .mfa-btn:hover{
          transform: translateY(-1px);
          box-shadow: 0 14px 28px rgba(0,0,0,.08) !important;
          filter: brightness(1.01);
        }
        .mfa-stack .mfa-btn:active{
          transform: translateY(0px);
          filter: brightness(.99);
        }
        .mfa-stack .mfa-btn:disabled{
          opacity:.7;
          cursor:not-allowed;
          transform:none !important;
          box-shadow: 0 10px 24px rgba(0,0,0,.06) !important;
        }

        /* Primary (Confirm) */
        .mfa-stack .mfa-btn--primary{
          background:#2a0516 !important;
          color:#fff !important;
        }
        .mfa-stack .mfa-btn--primary:hover{
          background:#220412 !important;
        }

        /* Enable with Authenticator (orange) */
        .mfa-stack .mfa-btn--auth{
          background:#f15822 !important;
          color:#fff !important;
        }
        .mfa-stack .mfa-btn--auth:hover{
          background:#e54d1e !important;
        }

        /* Enable with Email (blue) */
        .mfa-stack .mfa-btn--email{
          background:#8cade1 !important;
          color:#0b1220 !important;
        }
        .mfa-stack .mfa-btn--email:hover{
          background:#789bd8 !important;
        }

        /* Green actions */
        .mfa-stack .mfa-btn--success{
          background:#16a34a !important;
          color:#fff !important;
        }
        .mfa-stack .mfa-btn--success:hover{
          background:#15803d !important;
        }

        /* Ghost (Cancel) */
        .mfa-stack .mfa-btn--ghost{
          background:#fff !important;
          color:#2a0516 !important;
          border-color:#e5e7eb !important;
        }
        .mfa-stack .mfa-btn--ghost:hover{
          background:#fafafa !important;
        }

        
        /* Manual setup key (mobile-friendly) */
        .mfa-stack .mfa-manual{
          margin-top: 10px;
          padding: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #fff;
        }
        .mfa-stack .mfa-manual__title{
          font-weight: 900;
          color: #2a0516;
          margin-bottom: 6px;
        }
        .mfa-stack .mfa-manual__text{
          color: #657899;
          font-size: 13px;
          line-height: 1.35;
          margin-bottom: 10px;
        }
        .mfa-stack .mfa-manual__row{
          display:flex;
          gap:10px;
          align-items:center;
          flex-wrap:wrap;
        }
        .mfa-stack .mfa-manual__code{
          padding: 10px 12px;
          border-radius: 10px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          font-weight: 800;
          letter-spacing: .5px;
          word-break: break-all;
          flex: 1 1 220px;
        }
        .mfa-stack .mfa-copy-btn{
          flex: 0 0 auto;
        }
        .mfa-stack .mfa-manual__hint{
          margin-top: 8px;
          color: #6b7280;
          font-size: 12px;
        }

        /* Status colour */
        .mfa-stack .mfa-status-val--on{ color:#15803d !important; }
        .mfa-stack .mfa-status-val--off{ color:#b91c1c !important; }
      `}</style>
    </div>
  );
}
